import { ButtonTypes, COMPONENT_TYPES, playerTypes } from '@libs/constants';
import { UpcomingList, UpcomingItem } from '@intigral/ui';
import h from '../../helpers/common';
import { __ } from '../../translations';
import { TransformedVOD } from '../../utils/vodTransformer';
import { Carousel } from '@intigral/domain/lib/core/entities/ScreenEntities';
import { useDispatch, useSelector } from 'react-redux';
import { PROVIDER } from '../../config/constants';
import AddReminderButton from '../elements/AddReminderButton';
import { Button, FontIcon } from '@intigral/shared';
import { checkShowLockIcon, isSubscriptionLockedContent } from '../../helpers/vod';
import { isFreeOrInactive } from '@intigral/utils/lib/user';
import { getUserInfo } from '../../utils/user';
import { IUserFullObject } from '@intigral/types';
import PlayVideoButton from '../elements/PlayVideoButton';
import { useConfigurationSections } from '../../hooks/useConfiguration';
import { showUpgradedModal } from '../../store/reducers/generalConfigurationSlice';
import { PROGRAM_TYPE } from '@intigral/constant';
import useToggleDetailOverlay from '../../hooks/useToggleDetailOverlay';
import { GenericBitmovinPlayer } from '../../features/player';
import useTimeToExpire from '../../hooks/useTimeToExpire';
import AddWatchlistButton from '../elements/AddWatchlistButton';
import { useReminderItem } from '../../hooks/useReminderItem';
import { useUserReminders } from '../../store/reducers/reminderSlice';
import { useContext, useState } from 'react';
import { getMixpanelVodProperties } from '../../contexts/MixpanelHelper';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import { EVENT } from '../../contexts/MixpanelConstants';
import { useTvod } from '../../hooks/useTvod';

type GenericVodCarouselProps = {
    isLoading?: boolean;
    meta: {
        title: {
            en: string;
            ar: string;
        };
    };
    content: TransformedVOD[];
    carousel?: Carousel;
    params?: {
        items_count: number;
        filter_params: any;
    };
    shouldShowNumbers?: boolean;
};

export default function GenericVerticalList({
    isLoading = false,
    meta,
    content,
    carousel,
    params,
    shouldShowNumbers = false,
    ...props
}: GenericVodCarouselProps) {
    const [activeItem, setActiveItem] = useState(null);
    return (
        <UpcomingList
            key={carousel?.id}
            // month={'Feb'}
            // year={'2025'}
            isLoading={isLoading}
            items={
                content?.map((item, indx) => {
                    return (
                        <GenericVerticalItem
                            item={item}
                            indx={indx}
                            carousel={carousel}
                            meta={meta}
                            shouldShowNumbers={shouldShowNumbers}
                            activeItem={activeItem}
                            setActiveItem={setActiveItem}
                        />
                    );
                }) ?? []
            }
        />
    );
}

const GenericVerticalItem = ({ item, indx, carousel, meta, shouldShowNumbers, activeItem, setActiveItem }) => {
    const userInfo = getUserInfo();
    // const [searchParams, setSearchParams] = useSearchParams();
    // Using React Query instead of Redux for configuration
    const { config, promotionProviderImages, autoTrailerCarousel, paidVsFree } = useConfigurationSections();
    const autoTrailerCarouselConfiguration = autoTrailerCarousel;
    const movieOverlay = useSelector((state: any) => state.movieOverlay);
    const enableLockIcon = isFreeOrInactive(userInfo as IUserFullObject) && paidVsFree?.lockIconEnable;
    const dispatch = useDispatch();
    const { showDetailOverlay } = useToggleDetailOverlay();
    const { data: reminderData } = useUserReminders({ detailed: false }, userInfo);
    const { isComingSoonItem, isReminded } = useReminderItem(item, reminderData ?? []);
    const { track } = useContext(MixpanelContext);

    const openDetailOverlay = (item) => {
        const itemId = item?.Id;
        const typeKey = item.programType === 'movie' ? 'movie' : 'series';
        showDetailOverlay({
            [typeKey]: btoa(itemId),
            guid: btoa(item?.guid),
            season: btoa(item?.tvSeasonNumber || item?.seasonNumber || null),
        });
        handleMixPanelEvents('detailOverlay');
        setActiveItem(null);
    };

    const getPoster = (item) => {
        if (item?.image_landscape) {
            return h.getImagizerUrl(
                item?.image_landscape,
                (autoTrailerCarouselConfiguration as any)?.landscape_image_width,
                (autoTrailerCarouselConfiguration as any)?.landscape_image_height
            );
        }

        return item?.image || '';
    };

    const getTrailer = (item) => {
        if (item?.trailerMediaUrl && (autoTrailerCarouselConfiguration as any)?.is_enabled) {
            return (
                <div className="bitholder">
                    <GenericBitmovinPlayer
                        streamingUrl={item?.trailerMediaUrl}
                        playerType={playerTypes.Trailer}
                        withSimpleUIInMinimizedMode
                        onFinish={() => setActiveItem(null)}
                        VODAsset={item}
                    />
                </div>
            );
        }
        return null;
    };

    const handleClickWatchList = (isAdded) => {
        if (!isAdded) {
            handleMixPanelEvents('addWatchlist');
        }
    };

    const handleMixPanelEvents = (type: 'reminder' | 'addWatchlist' | 'play' | 'detailOverlay' | 'playTrailer') => {
        const eventProps = {
            'Category Name': meta?.title?.en,
            ...getMixpanelVodProperties({
                ...item,
            }),
        };
        switch (type) {
            case 'reminder':
                track(EVENT.NEW_AND_HOT.CATEGORY_PAGE_REMINDER_CLICK, {
                    ...eventProps,
                });
                break;
            case 'addWatchlist':
                track(EVENT.NEW_AND_HOT.CATEGORY_PAGE_ADD_WACTHLIST_CLICK, {
                    ...eventProps,
                });
                break;
            case 'play':
                track(EVENT.NEW_AND_HOT.CATEGORY_PAGE_ITEM_PLAY, {
                    ...eventProps,
                });
                break;
            case 'detailOverlay':
                track(EVENT.NEW_AND_HOT.CATEGORY_PAGE_ITEM_CLICK, {
                    ...eventProps,
                });
                break;
            case 'playTrailer':
                track(EVENT.NEW_AND_HOT.CATEGORY_PAGE_PLAY_TRAILER, {
                    ...eventProps,
                });
                break;
        }
    };

    const isLocked = checkShowLockIcon(item?.subscriptionGuids ?? []) && enableLockIcon;
    const isTvod = item?.programType === PROGRAM_TYPE.MOVIE && item?.categories?.includes('TVOD');
    const locked = (!isTvod && isLocked) || item?.isLocked;
    const isSubscriptionLocked = isSubscriptionLockedContent(item, locked);
    return (
        <UpcomingItem
            id={item.Id}
            key={`upcoming-list-item-${carousel?.id}-${indx}-${item?.Id}`}
            top10index={shouldShowNumbers ? indx + 1 : null}
            title={item?.text}
            text={item?.description}
            when={''}
            video={!movieOverlay.isVisible && getTrailer(item)}
            poster={getPoster(item)}
            provider={h.getImagizerUrl(
                promotionProviderImages?.[item?.provider[0]]?.imageUrl,
                PROVIDER.WIDTH,
                PROVIDER.HEIGHT,
                PROVIDER.QUALITY
            )}
            ctas={{
                play: isComingSoonItem ? null : locked ? (
                    <Button
                        onClick={() => {
                            handleMixPanelEvents('play');
                            dispatch(showUpgradedModal(true));
                        }}
                        data-test-id="play-button"
                    >
                        {__('play_now')} <FontIcon name="lock" size="smaller" md="smaller" />
                    </Button>
                ) : (
                    <VideoPlayButton
                        item={item}
                        componentName={carousel.id}
                        meta={meta}
                        openDetailOverlay={openDetailOverlay}
                        mixpanelEventOnPlayClick={() => {
                            handleMixPanelEvents('play');
                        }}
                    />
                ),
                page: isSubscriptionLocked
                    ? undefined
                    : {
                          LinkComponent: 'a',
                          social: true,
                          secondary: true,
                          compact: true,
                          children: 'Page',
                          onClick: () => {
                              openDetailOverlay(item);
                          },
                      },
                reminderButton: isComingSoonItem ? (
                    <AddReminderButton
                        shouldCallMixPanelEvent={false}
                        item={item}
                        category={item?.category as 'VOD' | 'LINEAR'}
                        isComingSoon={isComingSoonItem}
                        onClickReminder={(_type) => {
                            handleMixPanelEvents('reminder');
                        }}
                    />
                ) : null,
                watchlistButton: (
                    // isComingSoonItem || isTvod ? <></> :
                    <AddWatchlistButton item={item} type={ButtonTypes.VodeItem} onClick={handleClickWatchList} />
                ),
            }}
            activeItem={activeItem}
            setActiveItem={(prop) => {
                handleMixPanelEvents('playTrailer');
                setActiveItem(prop);
            }}
            locked={locked}
            reminder={isComingSoonItem && isReminded}
        />
    );
};

const VideoPlayButton = ({ item, componentName, meta, openDetailOverlay, mixpanelEventOnPlayClick }) => {
    const { priceInfo, isTvod, expiryDate, isRented } = useTvod(item);
    const { priceWithVat = '', currency = '' } = priceInfo || {};
    const { hours, minutes } = useTimeToExpire(expiryDate);
    let tvodRentNowBtnText = __('tvod_rent_now_button_details') || '';

    const getPlayLabel = () => {
        const seasonNumber = item?.tvSeasonNumber || item?.seasonNumber;
        const episodeNumber = item?.tvSeasonEpisodeNumber || item?.episodeNumber;

        if (item?.programType === PROGRAM_TYPE.SERIES && seasonNumber && episodeNumber) {
            const pad = (n: number | string) => n.toString().padStart(2, '0');
            return `${__('watch_now')} S${pad(seasonNumber)}, E${pad(episodeNumber)}`;
        }

        return __('play_now');
    };

    if (priceWithVat) {
        tvodRentNowBtnText = tvodRentNowBtnText.replace('{priceWithVat}', `<label  dir="ltr"> ${priceWithVat}`);
    }
    if (tvodRentNowBtnText.includes('{priceWithVat}')) {
        tvodRentNowBtnText = tvodRentNowBtnText.replace(' - {priceWithVat}', ``);
    }
    tvodRentNowBtnText = tvodRentNowBtnText.replace('{currency}', `${currency}</label>`);
    // Replace <label> with <span>do
    tvodRentNowBtnText = tvodRentNowBtnText.replace(/<label(.*?)>/g, '<span$1>').replace(/<\/label>/g, '</span>');

    if (!isTvod || (isTvod && ((isRented && hours > 0) || minutes > 0))) {
        return (
            <PlayVideoButton
                shouldCallMixPanelEvent={false}
                buttonType={ButtonTypes.VodeItem}
                item={item}
                componentType={COMPONENT_TYPES.VOD_CAROUSEL}
                componentName={componentName}
                meta={meta}
                testId={'play-now-button'}
                onClick={mixpanelEventOnPlayClick}
            >
                {getPlayLabel()}
            </PlayVideoButton>
        );
    }

    if (isTvod && (!isRented || (hours <= 0 && minutes <= 0))) {
        return (
            <PlayVideoButton
                shouldCallMixPanelEvent={false}
                buttonType={ButtonTypes.TVodItem}
                item={item}
                componentType={COMPONENT_TYPES.VOD_CAROUSEL}
                componentName={componentName}
                meta={meta}
                fromOverlay={true}
                onClick={() => {
                    if (openDetailOverlay) {
                        openDetailOverlay(item);
                    }
                }}
                testId="rent-button"
            >
                <span dangerouslySetInnerHTML={{ __html: tvodRentNowBtnText }}></span>
            </PlayVideoButton>
        );
    }
};
